/**
 * Authenticated fetch helper: merges auth headers and opens the global session-expired modal on 401.
 */

import { store } from '@store/index';
import { openSessionExpiredModal } from '@store/slices/authSlice';

const SESSION_KEY = 'persist:pr-gen-session';

export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    // Auth is persisted to ``localStorage`` via redux-persist (see
    // store/index.ts) so the same Bearer token is visible to every tab
    // on this origin — necessary for the cross-tab deep-link from the
    // newsletter SOV pencil to the Brand SOV editor.
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      const persisted = JSON.parse(raw) as Record<string, string>;
      const auth = JSON.parse(persisted.auth || '{}') as {
        tokens?: { accessToken?: string };
      };
      if (auth.tokens?.accessToken) {
        headers.Authorization = `Bearer ${auth.tokens.accessToken}`;
      }
    }
  } catch {
    /* no token */
  }
  return headers;
}

/**
 * `fetch` with JSON defaults and centralized 401 → session modal (does not clear auth).
 */
export async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  const auth = getAuthHeaders();
  // When the body is FormData the browser must set Content-Type itself (with the
  // multipart boundary). Injecting application/json here strips the boundary and
  // causes FastAPI to return 422 Unprocessable Content.
  const isFormData = init?.body instanceof FormData;
  Object.entries(auth).forEach(([k, v]) => {
    if (isFormData && k === 'Content-Type') return;
    if (!headers.has(k)) headers.set(k, v);
  });

  const response = await fetch(input, {
    ...init,
    credentials: 'include',
    headers,
  });

  if (response.status === 401) {
    store.dispatch(openSessionExpiredModal());
    throw new Error('Session expired');
  }

  return response;
}
